import { useState, useEffect } from 'react';
import { notification } from 'antd';
import { analyzeAndUpdateWorkflow } from '@/utils/analysisWorkflow';
import { WorkflowType } from 'src/enums/EWorkflow';
import ShortUniqueId from 'short-unique-id';
import { IUseBookingRequestLogicProps, IWorkflow } from '@/interfaces/ICalendarWorkflows';
import { safeJSONParse } from '@/utils/helper';

const uid = new ShortUniqueId({ length: 10 });

export const useBookingRequestLogic = ({
	clientObject,
	mutation,
	refetch,
	selectedAgentId,
	dispatch,
	closeModal,
}: IUseBookingRequestLogicProps) => {
	const [existingWorkflows, setExistingWorkflows] = useState<IWorkflow[]>([]);

	useEffect(() => {
		let allExistingWorkflows: IWorkflow[] = [];

		const beginningQuestions = safeJSONParse(clientObject?.intake_questions, []);

		if (beginningQuestions && beginningQuestions.length > 0) {
			allExistingWorkflows.push({
				customScenario: '',
				description: 'beginning',
				questions: beginningQuestions,
				intake_beginning_natural: clientObject.intake_beginning_natural,
				configEnabled: false,
				fallbackMessage: '',
			});
		}

		if (clientObject?.intake_questions_workflows) {
			try {
				const workflowData = safeJSONParse(clientObject?.intake_questions_workflows, []);
				allExistingWorkflows = [...allExistingWorkflows, ...workflowData];
			} catch {
				notification.error({ message: 'Failed to parse existing workflows' });
			}
		}

		setExistingWorkflows(allExistingWorkflows);
	}, [
		clientObject?.intake_questions_workflows,
		clientObject?.intake_questions,
		clientObject?.intake_beginning_natural,
	]);

	const handleSubmit = async (
		values: any,
		isEditing: boolean,
		currentWorkflow: IWorkflow | null,
	) => {
		try {
			let formBuilders: IWorkflow[] = [];
			let allWorkflows = [...existingWorkflows];

			if (isEditing && currentWorkflow) {
				const updatedScenario = values.scenarios.map((scenario: any) => ({
					...currentWorkflow,
					customScenario: scenario.customScenario,
					questions: scenario?.questions?.map((q: any) => q.question),
					postExecutionPrompt: scenario?.postExecutionPrompt,
					availability: scenario?.availability,
					configEnabled: scenario?.configEnabled ?? false,
					fallbackMessage: scenario?.fallbackMessage || '',
				}));

				allWorkflows = allWorkflows.map((workflow) =>
					workflow.id === currentWorkflow.id ? updatedScenario[0] : workflow,
				);
				formBuilders = allWorkflows;
			} else {
				const updatedScenario = values?.scenarios.map((scenario: any) => ({
					id: scenario.id ?? uid.rnd(),
					customScenario: scenario.customScenario,
					description: 'custom-scenario',
					questions: scenario?.questions?.map((q: any) => q.question),
					postExecutionPrompt: scenario?.postExecutionPrompt,
					availability: scenario?.availability,
					configEnabled: scenario?.configEnabled ?? false,
					fallbackMessage: scenario?.fallbackMessage || '',
					bookingRequest: true,
				}));
				formBuilders = [...existingWorkflows, ...updatedScenario];
			}

			formBuilders = formBuilders.map((scenario) => ({
				...scenario,
				description: scenario.customScenario === '' ? 'beginning' : 'custom-scenario',
			}));

			const beginningWorkflow = formBuilders.find(
				(workflow) => workflow.description === 'beginning',
			);
			const naturalIntake = beginningWorkflow?.intake_beginning_natural;

			await mutation.mutateAsync({
				formBuilders,
				intake_beginning_natural: naturalIntake,
				agentId: selectedAgentId,
			});

			await refetch();

			notification.success({
				message: isEditing
					? 'Booking request workflow updated successfully!'
					: 'Booking request workflow added successfully!',
				duration: 3,
			});

			closeModal();

			await analyzeAndUpdateWorkflow(formBuilders, WorkflowType.INTAKE, dispatch, selectedAgentId);
		} catch (err) {
			notification.error({ message: 'Something went wrong, please try again later!' });
			throw err;
		}
	};

	const getDefaultFormValues = () => ({
		scenarios: [
			{
				customScenario: 'Collect caller availability when they want to schedule an appointment',
				questions: [
					{ question: 'What days of the week work best for you?' },
					{ question: 'What time of day do you prefer (morning, afternoon, evening)?' },
					{ question: 'Do you have any specific dates in mind?' },
					{ question: 'What is the reason for your appointment?' },
				],
				postExecutionPrompt:
					'Thank the caller for providing their availability information and let them know someone will contact them within 24 hours to confirm the appointment details.',
			},
		],
	});

	const getEditFormValues = (workflow: IWorkflow) => ({
		scenarios: [
			{
				customScenario: workflow.customScenario,
				questions: workflow.questions?.map((q: string) => ({ question: q })) || [],
				postExecutionPrompt: workflow.postExecutionPrompt,
			},
		],
	});

	return {
		existingWorkflows,
		handleSubmit,
		getDefaultFormValues,
		getEditFormValues,
	};
};
